import { interventionTypes } from "../../constants";

/**
 * Mapping of intervention groups to their corresponding intervention codes.
 * Each key represents a group name, and the corresponding value is an array of intervention codes.
 * @type {Object.<string, string[]>}
 */
export const GROUP_MAP = {
  pa_simple: [interventionTypes.elkoninBox3],
  rhyme: [interventionTypes.rhyming1, interventionTypes.rhymingQuiz1],
  decode_simple: [
    interventionTypes.phonemeLipsync,
    interventionTypes.soundout2,
    interventionTypes.soundout3,
  ], // split on decode_simple for low wcpm only, higher phoneme counts (>3)
  erss_all: [
    interventionTypes.elkoninBox1,
    interventionTypes.elkoninBox4,
    interventionTypes.changeOnePart1,
    interventionTypes.phonemeLipsync,
    interventionTypes.sayASound,
    interventionTypes.upAndDown1,
    interventionTypes.upAndDown3,
    interventionTypes.soundSeeker,
    interventionTypes.soundout2,
    interventionTypes.letterFlies,
  ], // erss - all eligible
  erss_no_cop: [
    interventionTypes.elkoninBox1,
    interventionTypes.elkoninBox4,
    interventionTypes.phonemeLipsync,
    interventionTypes.sayASound,
    interventionTypes.upAndDown1,
    interventionTypes.upAndDown3,
    interventionTypes.soundSeeker,
    interventionTypes.soundout2,
    interventionTypes.letterFlies,
  ], // erss without changeonepart1 (words with semi-vowels)
  decode_elkonin: [
    interventionTypes.elkoninBox1,
    interventionTypes.elkoninBox2,
    interventionTypes.elkoninBox4,
    interventionTypes.phonemeLipsync,
  ], // split on decode_simple, lower phoneme counts (<=3)
  decode_up_and_down: [
    ...Array(2).fill(interventionTypes.upAndDown1),
    interventionTypes.upAndDown2,
    interventionTypes.upAndDown3,
  ],
  letterflies: [interventionTypes.letterFlies],
  lipsync: [interventionTypes.phonemeLipsync, interventionTypes.lipsync],
  wordinvestigator: [
    interventionTypes.wordInvestigator1,
    interventionTypes.wordInvestigator2,
  ],
  encoding_special: [
    interventionTypes.changeOnePart1,
    interventionTypes.giveAndTake1,
    interventionTypes.wordScramble1,
  ], // not on words has semi vowels
  strictly_decode: [
    interventionTypes.sayASound,
    interventionTypes.sayOneSound1,
    interventionTypes.soundSeeker,
    interventionTypes.phonemeLipsync,
  ],
  sight: [
    interventionTypes.sight,
    interventionTypes.phonemeLipsync,
    interventionTypes.sayOneSound1,
    interventionTypes.sayASound,
  ],
  vocab_only: [
    interventionTypes.simpledefinition,
    interventionTypes.vocabularyWithPictures,
  ],
  name: [interventionTypes.name1, interventionTypes.name2],
  tense: [interventionTypes.tense],
  morpheme: [interventionTypes.morpheme],
  el_spanish: [interventionTypes.cognate],
  clap: [interventionTypes.clap],
  worddisplay: [interventionTypes.wordDisplay, interventionTypes.riddle],
  bk: [...Array(3).fill(interventionTypes.funfact), interventionTypes.riddle],
};

/**
 * Mapping of ranked list types to their corresponding lists of intervention groups.
 * Each key represents an ranked list type, and the corresponding value is an array of intervention groups.
 * @type {Object.<string, string[]>}
 */
export const TYPE_MAP = {
  A01: ["erss_all"], // erss
  A11: ["erss_no_cop"], // erss
  B01: ["erss_all"], // erss
  B11: ["erss_all"], // erss
  B21: ["erss_no_cop"], // erss
  B31: ["erss_no_cop"], // erss
  B41: ["erss_all"], // erss
  C01: ["erss_no_cop"], // erss

  A: ["pa_simple", "strictly_decode", "decode_elkonin"],
  A1: ["rhyme", "decode_simple", "vocab_only"],
  B: ["decode_simple", "vocab_only", "bk"],
  B1: ["decode_elkonin", "wordinvestigator"],
  B2: ["vocab_only", "bk", "decode_simple"],
  B3: ["wordinvestigator", "decode_elkonin"],
  B4: ["encoding_special", "decode_up_and_down", "decode_elkonin"],
  C: ["decode_up_and_down", "wordinvestigator", "pa_simple"],
  D: ["sight", "strictly_decode", "wordinvestigator", "bk"],
  E: ["vocab_only", "clap"],
  E1: ["vocab_only", "bk"],
  F: ["clap", "vocab_only", "lipsync", "bk"],
  G: ["vocab_only", "clap", "bk"],
  H: ["worddisplay", "vocab_only"],
  I: ["lipsync", "clap", "vocab_only"],
  L: ["name", "lipsync"],
  M: ["name", "lipsync"],
  N: ["morpheme", "vocab_only", "clap"],
  K: ["bk", "worddisplay", "vocab_only"],
  S: ["lipsync", "clap"],
};

/**
 * Mapping of ranked list types to their corresponding weight values for interventions.
 * Each key represents a ranked list type, and the corresponding value is an array of weights that corresponds to the intervention group in TYPE_MAP.
 * @type {Object.<string, number[]>}
 */
export const TOP_N_RANKING = {
  A01: [1], // erss
  A11: [1], // erss
  B01: [1], // erss
  B11: [1], // erss
  B21: [1], // erss
  B31: [1], // erss
  B41: [1], // erss
  C01: [1], // erss

  A: [2, 1, 1],
  A1: [3, 1, 1],
  B: [2, 1, 1],
  B1: [2, 1],
  B2: [3, 2, 1],
  B3: [1, 1],
  B4: [3, 3, 1],
  C: [3, 3, 1],
  D: [1, 1, 1, 1],
  E: [4, 1],
  E1: [2, 1],
  F: [1, 1, 1, 1],
  G: [1, 1, 1],
  H: [1, 1],
  I: [3, 2, 1],
  L: [2, 1],
  M: [3, 1],
  N: [2, 1, 1],
  K: [2, 2, 1],
  S: [1, 1],
};
